import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Roles } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { asCollection } from '../lib/collections'
import { formatMenuInstant } from '../lib/date'
import { getSystemNoticeCategoryBadgeClass, getSystemNoticeCategoryLabel } from '../lib/systemNoticeCategory'
import { useToast } from '../ui/ToastProvider'
import ListContainer from '../ui/ListContainer'
import MenuGuideButton from '../components/MenuGuideButton'

export default function SystemNoticesPage() {
    const { hasAnyRole } = useAuth()
    const navigate = useNavigate()
    const { show } = useToast()
    const [searchParams, setSearchParams] = useSearchParams()
    const [page, setPage] = useState(Number(searchParams.get('page') || '1'))
    const [size] = useState(Number(searchParams.get('size') || '20'))
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [total, setTotal] = useState(0)

    const isOperator = hasAnyRole ? hasAnyRole([Roles.OPERATOR]) : false

    // URL 동기화
    useEffect(() => {
        const next = new URLSearchParams()
        if (page && page !== 1) next.set('page', String(page))
        if (size && size !== 20) next.set('size', String(size))
        setSearchParams(next, { replace: true })
    }, [page, size, setSearchParams])

    const listQuery = useQuery({
        queryKey: ['system-notices', { page, size, opAll: isOperator }],
        queryFn: async () => {
            const params = { page: page - 1, size }
            if (isOperator) {
                params.include_all_periods = true
            }
            const res = await api.get('/system-notices', { params })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const raw = asCollection(payload)
            const totalElements = Number(payload?.totalElements ?? payload?.total ?? raw.length ?? 0)
            return { items: raw, total: totalElements }
        },
        keepPreviousData: true,
    })

    useEffect(() => {
        const data = listQuery.data?.items || []
        setItems(data)
        setTotal(listQuery.data?.total || 0)
        setLoading(listQuery.isFetching)
        setError(listQuery.isError ? '목록을 불러오지 못했습니다' : null)
    }, [listQuery.data, listQuery.isFetching, listQuery.isError])

    const handleDelete = async (e, noticeId) => {
        e.stopPropagation()
        if (!window.confirm('정말 삭제하시겠습니까?')) return
        try {
            await api.delete(`/system-notices/${noticeId}`)
            show('삭제되었습니다', 'success')
            listQuery.refetch()
        } catch (err) {
            show('삭제에 실패했습니다', 'error')
        }
    }

    const header = (
        <div className="p-4 border-b bg-white">
            <div className="flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl font-bold">운영자 공지</h1>
                    <MenuGuideButton slug="ops-management" title="운영관리 가이드" />
                </div>
                {isOperator && (
                    <Link
                        to="/ops/system-notices/new"
                        className="inline-flex items-center gap-2 rounded-lg bg-brand-sky px-4 py-2 text-sm font-semibold text-white hover:bg-brand-sky/90"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                            <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                        </svg>
                        작성
                    </Link>
                )}
            </div>
        </div>
    )

    const scheduleState = (notice) => {
        const now = Date.now()
        if (notice.endAt) {
            const t = new Date(notice.endAt).getTime()
            if (!Number.isNaN(t) && t < now) return { key: 'ended', label: '노출 종료' }
        }
        if (notice.startAt) {
            const t = new Date(notice.startAt).getTime()
            if (!Number.isNaN(t) && t > now) return { key: 'future', label: '노출 예정' }
        }
        return null
    }

    const table = items.length > 0 ? (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-4 py-2 text-left">제목</th>
                    <th className="px-4 py-2 text-left">표시 설정</th>
                    <th className="px-4 py-2 text-left">우선순위</th>
                    <th className="px-4 py-2 text-left">기간</th>
                    <th className="px-4 py-2 text-left">작성일</th>
                    {isOperator && (
                        <th className="px-4 py-2 text-right">관리</th>
                    )}
                </tr>
            </thead>
            <tbody>
                {items.map((notice) => {
                    const sched = isOperator ? scheduleState(notice) : null
                    return (
                    <tr
                        key={notice.id}
                        onClick={() => navigate(`/ops/system-notices/${notice.id}`)}
                        className="cursor-pointer hover:bg-slate-50"
                    >
                        <td className="px-4 py-2">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${getSystemNoticeCategoryBadgeClass(notice.category)}`}>
                                    {getSystemNoticeCategoryLabel(notice.category)}
                                </span>
                                <span className="font-medium text-slate-900">{notice.title}</span>
                            </div>
                        </td>
                        <td className="px-4 py-2">
                            <div className="flex flex-wrap gap-1">
                                {notice.showAsPopup && (
                                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                                        팝업
                                    </span>
                                )}
                                {notice.showInBoard && (
                                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                                        대시보드
                                    </span>
                                )}
                                {notice.notifyWeb && (
                                    <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700">
                                        WEB
                                    </span>
                                )}
                                {notice.notifyApp && (
                                    <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-700">
                                        APP
                                    </span>
                                )}
                            </div>
                        </td>
                        <td className="px-4 py-2 text-slate-600">{notice.priority || 0}</td>
                        <td className="px-4 py-2 text-slate-600">
                            {notice.startAt && notice.endAt ? (
                                <div className="text-xs">
                                    {formatMenuInstant(notice, 'startAt')} ~ {formatMenuInstant(notice, 'endAt')}
                                    {sched && (
                                        <span
                                            className={
                                                sched.key === 'ended'
                                                    ? 'ml-2 inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-700'
                                                    : 'ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800'
                                            }
                                        >
                                            {sched.label}
                                        </span>
                                    )}
                                </div>
                            ) : notice.startAt ? (
                                <div className="text-xs">
                                    {formatMenuInstant(notice, 'startAt')} ~
                                    {sched && (
                                        <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                            {sched.label}
                                        </span>
                                    )}
                                </div>
                            ) : notice.endAt ? (
                                <div className="text-xs">
                                    ~ {formatMenuInstant(notice, 'endAt')}
                                    {sched && (
                                        <span className="ml-2 inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                                            {sched.label}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <span className="text-slate-400">-</span>
                            )}
                        </td>
                        <td className="px-4 py-2 text-slate-600">
                            {formatMenuInstant(notice, 'createdAt')}
                        </td>
                        {isOperator && (
                            <td className="px-4 py-2 text-right">
                                <div className="flex items-center justify-end gap-2">
                                    <Link
                                        to={`/ops/system-notices/${notice.id}/edit`}
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-xs text-brand-sky hover:underline"
                                    >
                                        수정
                                    </Link>
                                    <button
                                        type="button"
                                        onClick={(e) => handleDelete(e, notice.id)}
                                        className="text-xs text-red-600 hover:underline"
                                    >
                                        삭제
                                    </button>
                                </div>
                            </td>
                        )}
                    </tr>
                    );
                })}
            </tbody>
        </table>
    ) : null

    const footer = total > 0 ? (
        <div className="p-4 border-t bg-white flex items-center justify-between">
            <div className="text-sm text-slate-600">
                총 {total}건 (페이지 {page} / {Math.ceil(total / size)})
            </div>
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded-lg border border-slate-300 px-3 py-1 text-sm disabled:opacity-50"
                >
                    이전
                </button>
                <button
                    type="button"
                    onClick={() => setPage(p => Math.min(Math.ceil(total / size), p + 1))}
                    disabled={page >= Math.ceil(total / size)}
                    className="rounded-lg border border-slate-300 px-3 py-1 text-sm disabled:opacity-50"
                >
                    다음
                </button>
            </div>
        </div>
    ) : null

    return (
        <ListContainer
            loading={loading}
            error={error}
            onRetry={() => listQuery.refetch()}
            header={header}
            table={table}
            footer={footer}
            emptyText="운영자 공지가 없습니다"
        />
    )
}
